import { produce } from "immer";
import { omit, pick } from "lodash";
import {
    AndFilter,
    Experience,
    PurchaseRule,
    PurchaseRuleAction,
    PurchaseRuleFilterObject,
    PurchaseRuleFilterOperator,
    PurchaseRuleLink,
    PurchaseRuleLinkEntityObject,
    PurchaseRuleLinkTag,
    RuleFilterItem,
    Seller,
} from "types";
import { api } from "../../../api/api";
import { PreferenceLabels } from "../../../models/DuplicateExperience";
import { omitId, saveData } from "./DuplicateHelper";
import { SchedulesMap } from "./DuplicateSchedulesHelper";

const allowedPricingPurchaseRulesMetaFields = ["tieredPricing"];

interface getPricingPurchaseRulePayloadProps {
    purchaseRule: PurchaseRule;
    experience: Experience;
    originalExperience: Experience;
    schedulesMap: SchedulesMap;
}

export const getPricingPurchaseRulePayload = ({
    purchaseRule,
    experience,
    originalExperience,
    schedulesMap,
}: getPricingPurchaseRulePayloadProps): PurchaseRule => {
    const initialFilters = purchaseRule.filter?.filters ?? [];
    const initialInProductScheduleFilter = initialFilters.find(
        (filter) => filter.operator === PurchaseRuleFilterOperator.IN_PRODUCT_SCHEDULE,
    );
    const inProductScheduleFilter = produce(initialInProductScheduleFilter, (draft) => {
        if (draft && "product" in draft) {
            const newScheduleItems = getNewPurchaseRuleFilterSchedules(draft.schedules.items, schedulesMap);
            draft.product = { id: experience.id };
            draft.schedules = { ...draft.schedules, items: newScheduleItems };
        }
    });

    const filter = produce(purchaseRule.filter, (draft) => {
        if (draft?.filters) {
            const newFilters = initialFilters.filter(
                (filter) => filter.operator !== PurchaseRuleFilterOperator.IN_PRODUCT_SCHEDULE,
            );

            if (inProductScheduleFilter) {
                newFilters.push(inProductScheduleFilter);
            }

            draft.filters = newFilters;
        }
    });

    return {
        ...omit(purchaseRule, ["id", "permanentId", "createdAt", "updatedAt", "createdBy"]),
        actions: purchaseRule.actions.map((action) => omitId<PurchaseRuleAction>(action)),
        name: purchaseRule.name?.replace(experience.name, originalExperience.name),
        meta: pick(purchaseRule.meta, allowedPricingPurchaseRulesMetaFields),
        tags: purchaseRule.tags?.map((tag) => pick(tag, ["id"])),
        filter,
    };
};

interface savePricingRulesAndLinksProps {
    pricingPurchaseRuleLink: PurchaseRuleLink;
    experience: Experience;
    originalExperience: Experience;
    seller: Seller;
    schedulesMap: SchedulesMap;
}

export const savePricingRulesAndLinks = async ({
    pricingPurchaseRuleLink,
    experience,
    originalExperience,
    seller,
    schedulesMap,
}: savePricingRulesAndLinksProps) => {
    const purchaseRulesToLink: Array<{ purchaseRule: { id: string }; sequence: number }> = [];

    await Promise.all(
        pricingPurchaseRuleLink.purchaseRules.map(async (purchaseRule) => {
            const rule = purchaseRule.purchaseRule as PurchaseRule;
            const payload = getPricingPurchaseRulePayload({
                purchaseRule: rule,
                experience,
                originalExperience,
                schedulesMap,
            });

            const { data } = await api.post<Required<PurchaseRule>>(`/purchaseRules`, payload);

            purchaseRulesToLink.push({ purchaseRule: { id: data.id }, sequence: purchaseRule.sequence });
        }),
    );

    const payload = {
        entities: [{ id: experience.id, object: PurchaseRuleLinkEntityObject.EXPERIENCE }],
        purchaseRules: purchaseRulesToLink,
        tags: [{ id: PurchaseRuleLinkTag.PRICING }],
        seller: { id: seller.id },
    };
    const path = `/purchaseRuleLinks`;
    const item = "pricing";

    await saveData({ payload, path, item });
};

interface getPurchaseRuleLinkPayloadProps {
    purchaseRuleLink: PurchaseRuleLink;
    experience: Experience;
}

export const getFeePurchaseRuleLinkPayload = async ({
    purchaseRuleLink,
    experience,
}: getPurchaseRuleLinkPayloadProps) => {
    return {
        entities: [
            ...purchaseRuleLink.entities,
            { id: experience.id, object: PurchaseRuleLinkEntityObject.EXPERIENCE },
        ],
    };
};

export const updateInFilterByFieldName = (filter: AndFilter | RuleFilterItem, fieldName: string, experienceId: ID) => {
    if ("operand" in filter && filter.object === PurchaseRuleFilterObject.IN_FILTER && filter.field === fieldName) {
        filter.operand = [...filter.operand!, experienceId];
    }

    if ("filters" in filter && Array.isArray(filter.filters) && filter.filters.length > 0) {
        for (const subFilter of filter.filters) {
            updateInFilterByFieldName(subFilter, fieldName, experienceId);
        }
    }
};

const getFeePurchaseRulePayload = (purchaseRule: PurchaseRule, experienceId: ID) => {
    const updatedFilter = produce(purchaseRule.filter, (draft) => {
        return draft ? updateInFilterByFieldName(draft, "productForTaxFees.id", experienceId) : null;
    });

    return {
        ...omit(purchaseRule, ["createdAt", "updatedAt", "createdBy"]),
        actions: purchaseRule.actions,
        filter: updatedFilter,
    };
};

interface SaveFeesProps {
    feePurchaseRuleLinks: PurchaseRuleLink[];
    experience: Experience;
}

export const saveFees = async ({ feePurchaseRuleLinks, experience }: SaveFeesProps) => {
    await Promise.all(feePurchaseRuleLinks.map(async (purchaseRuleLink) => saveTaxOrFee(purchaseRuleLink, experience)));
};

const saveTaxOrFee = async (purchaseRuleLink: PurchaseRuleLink, experience: Experience) => {
    const payload = await getFeePurchaseRuleLinkPayload({
        purchaseRuleLink,
        experience,
    });
    const item = PreferenceLabels.FEES;

    await Promise.all(
        purchaseRuleLink.purchaseRules.map(async (linkPurchaseRule) => {
            const purchaseRule = linkPurchaseRule.purchaseRule as PurchaseRule;
            const rulePayload = getFeePurchaseRulePayload(purchaseRule, experience.id);

            await saveData({
                payload: rulePayload,
                path: `/purchaseRules/${purchaseRule.id}`,
                item: item,
            });
        }),
    );

    const path = `/purchaseRuleLinks/${purchaseRuleLink.id}`;
    return saveData({ payload, path, item });
};

const getNewPurchaseRuleFilterSchedules = (oldSchedules: string[], schedulesMap: SchedulesMap) => {
    const newSchedules = oldSchedules.map((oldSchedule) => {
        const schedule = schedulesMap.find((schedule) => schedule.old === oldSchedule);

        return schedule?.new;
    });

    return newSchedules.filter(Boolean) as string[];
};
